import { use } from 'react';

import { CurrentUserContext } from '@/context/CurrentUserContext';
import type { FileFieldsFragment } from '@/gql/graphql';

export interface OwnerPermisson {
  _id: string | null;
  userCanEditFiles: boolean;
  userCanLockFiles: boolean;
  userCanPreventDownloadOfFiles: boolean;
  userCanShareFiles: boolean;
}

export interface FilePermission {
  canLock: boolean;
  canEdit: boolean;
  canShare: boolean;
  canUpload: boolean;
  canPreventDownload: boolean;
  canLockRoot: boolean;
  canPreventDownloadRoot: boolean;
  canSign: boolean;
  canUnsignCount: number;
}

export const useFilePermissions = (
  file: FileFieldsFragment | null | undefined,
  permissions: OwnerPermisson[],
  rootOwnerId: string | null,
) => {
  const { currentUserId } = use(CurrentUserContext);

  const ownerId = file?.owner?._id;
  const permission = ownerId
    ? permissions.find((item) => item && item._id === ownerId)
    : null;
  const rootPermission = permissions.find((item) => item._id === rootOwnerId);

  const signatures = file?.signatures ?? [];
  const canUnsignCount = signatures.filter((sig) => sig?.userCanUnsign).length;
  const userIsSigner = signatures.some(
    (sig) => currentUserId && sig?.signedBy?._id === currentUserId,
  );

  const canLock = Boolean(permission?.userCanLockFiles);
  const canPreventDownload = Boolean(permission?.userCanPreventDownloadOfFiles);
  const canLockRoot = Boolean(rootPermission?.userCanLockFiles);
  const canPreventDownloadRoot = Boolean(
    rootPermission?.userCanPreventDownloadOfFiles,
  );
  const canUpload = Boolean(file?.folder && permission?.userCanEditFiles);
  const canEdit = Boolean(
    permission?.userCanEditFiles && (canLock || !file?.adminLock),
  );
  const canShare = Boolean(permission?.userCanShareFiles);
  const canSign = Boolean(file?.userCanSign && !userIsSigner);

  return {
    canLock,
    canEdit,
    canShare,
    canUpload,
    canPreventDownload,
    canLockRoot,
    canPreventDownloadRoot,
    canSign,
    canUnsignCount,
  };
};
